import { Reference } from '@approvalmax/types';
import { useAtom, useAtomValue } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { NetSuiteItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { useCallback, useEffect, useMemo } from 'react';
import { useGetNetSuiteItemPriceLevels } from 'shared/data/webApp/v2';

import { NetSuitePriceLevel } from '../../data/types';
import { checkInvoiceListItemIsEmpty, convertIntegrationCodeToDocumentType, reorderLines } from '../../helpers';
import {
    useChangeNetSuiteRequest,
    useCurrencyPrecision,
    useGetAndIncreaseItemLineNumber,
    useUpdateLineCustomField,
} from '../../hooks';
import {
    netSuiteInvoiceFieldValuesState,
    netSuiteInvoiceItemListState,
} from '../../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.states';
import {
    calculateLineAmounts,
    createDefaultInvoiceLine,
    getBasePriceLevel,
    mapPriceLevelList,
    mapPriceLevels,
    updateInvoiceLineFieldValue,
} from './useNetSuiteInvoiceItemList.helpers';
import {
    InvoiceFieldUpdateHandler,
    InvoiceLineItemUpdateHandler,
    InvoiceMultiLineFieldUpdateHandler,
} from './useNetSuiteInvoiceItemList.types';

export const useNetSuiteInvoiceItemList = () => {
    const [invoiceItemList, setInvoiceItemList] = useAtom(netSuiteInvoiceItemListState);

    const getAndIncreaseItemLineNumber = useGetAndIncreaseItemLineNumber();

    const { currency } = useAtomValue(netSuiteInvoiceFieldValuesState);

    const { currencies } = useSelector(getNetSuiteContext);

    const { companyId } = useSelector(requestSelector);

    const currencyPrecision = useCurrencyPrecision(currency);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const { data, isLoading } = useGetNetSuiteItemPriceLevels({
        path: {
            companyId,
            documentType: convertIntegrationCodeToDocumentType(domain.IntegrationCode.NetSuiteInvoice),
        },
    });

    const priceLevels = useMemo(() => mapPriceLevels(data), [data]);

    useEffect(() => {
        if (!invoiceItemList.length) {
            const lineNumber = getAndIncreaseItemLineNumber();

            setInvoiceItemList([createDefaultInvoiceLine(lineNumber)]);
        }
    }, [invoiceItemList, setInvoiceItemList, getAndIncreaseItemLineNumber]);

    useEffect(() => {
        if (priceLevels) {
            setInvoiceItemList((current) =>
                current.map((line) => {
                    if (!line.item) {
                        return line;
                    }

                    const salesPrices = line.salesPrices.find((salesPrice) => salesPrice?.currencyId === currency?.id);
                    const priceLevelList = mapPriceLevelList(priceLevels, salesPrices);
                    const priceLevel = line.priceLevel ?? getBasePriceLevel(priceLevelList);
                    const priceLevelItem = priceLevel ? priceLevelList.find(({ id }) => id === priceLevel.id) : null;

                    return {
                        ...line,
                        priceLevel,
                        priceLevelList,
                        defaultRate: priceLevelItem?.price ?? null,
                    };
                })
            );
        }
    }, [priceLevels, currency, setInvoiceItemList]);

    const onAddLine = useCallback(() => {
        const lineNumber = getAndIncreaseItemLineNumber();

        setInvoiceItemList((current) => [...current, createDefaultInvoiceLine(lineNumber)]);

        changeNetSuiteRequest();
    }, [getAndIncreaseItemLineNumber, setInvoiceItemList, changeNetSuiteRequest]);

    const onReorderLines = useCallback(
        (oldIndex: number, newIndex: number) => {
            setInvoiceItemList((current) => reorderLines(current, oldIndex, newIndex));

            changeNetSuiteRequest();
        },
        [setInvoiceItemList, changeNetSuiteRequest]
    );

    const onRemoveLine = useCallback(
        (id: string) => {
            setInvoiceItemList((current) => current.filter((item) => item.id !== id));

            changeNetSuiteRequest();
        },
        [setInvoiceItemList, changeNetSuiteRequest]
    );

    const onCloneLine = useCallback(
        (id: string) => {
            setInvoiceItemList((current) => {
                const targetLine = current.find((line) => line.id === id);

                if (!targetLine) {
                    return current;
                }

                const lineNumber = getAndIncreaseItemLineNumber();

                return [
                    ...current,
                    {
                        ...targetLine,
                        lineNumber,
                        id: uniqueId('netSuiteItemLine-'),
                    },
                ];
            });

            changeNetSuiteRequest();
        },
        [getAndIncreaseItemLineNumber, setInvoiceItemList, changeNetSuiteRequest]
    );

    const onItemChange = useCallback(
        (id: string, value: NetSuiteItem | null) => {
            setInvoiceItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    if (!value) {
                        const newLine = {
                            ...line,
                            item: null,
                            units: null,
                            unitList: [],
                        };

                        newLine.isEmpty = checkInvoiceListItemIsEmpty(newLine);

                        return newLine;
                    }

                    const unitList = value.unitList || [];
                    const salesPrices = value.salesPrices.find((salesPrice) => salesPrice?.currencyId === currency?.id);
                    const priceLevelList = mapPriceLevelList(priceLevels, salesPrices);
                    const baseUnit = unitList.find((unit) => unit.isItemPrimaryPurchaseUnit);
                    const basePriceLevel = getBasePriceLevel(priceLevelList);

                    return {
                        ...line,
                        ...calculateLineAmounts(basePriceLevel, line.quantity, line.taxRate, currencyPrecision),
                        unitList,
                        priceLevelList,
                        item: value,
                        description: value.purchaseDescription,
                        units: baseUnit || null,
                        priceLevel: basePriceLevel,
                        salesPrices: value.salesPrices || [],
                        isEmpty: false,
                    };
                })
            );

            changeNetSuiteRequest();
        },
        [priceLevels, currency, currencyPrecision, setInvoiceItemList, changeNetSuiteRequest]
    );

    const onPriceLevelChange = useCallback(
        (id: string, value: NetSuitePriceLevel | null) => {
            setInvoiceItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    return {
                        ...line,
                        ...calculateLineAmounts(value, line.quantity, line.taxRate, currencyPrecision),
                        priceLevel: value,
                    };
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setInvoiceItemList, changeNetSuiteRequest]
    );

    const onFieldUpdate = useCallback<InvoiceFieldUpdateHandler>(
        (id, key, value) => {
            setInvoiceItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    return updateInvoiceLineFieldValue(line, key, value, currencyPrecision);
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setInvoiceItemList, changeNetSuiteRequest]
    );

    const onMultiLineFieldUpdate = useCallback<InvoiceMultiLineFieldUpdateHandler>(
        (key, value) => {
            setInvoiceItemList((current) =>
                current.map((line) => updateInvoiceLineFieldValue(line, key, value, currencyPrecision))
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, setInvoiceItemList, changeNetSuiteRequest]
    );

    const updateLineItem = useCallback<InvoiceLineItemUpdateHandler>(
        (id, data) => {
            setInvoiceItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    const newLine = {
                        ...line,
                        ...data,
                    };

                    newLine.isEmpty = checkInvoiceListItemIsEmpty(newLine);

                    return newLine;
                })
            );

            changeNetSuiteRequest();
        },
        [setInvoiceItemList, changeNetSuiteRequest]
    );

    const updateItemListOnCurrencyChange = useCallback(
        (newCurrency: Reference | null) => {
            setInvoiceItemList((current) => {
                const newCurrencyPrecision = currencies.find(({ id }) => id === newCurrency?.id)?.precision ?? 2;

                return current.map((line) => {
                    const salesPrices = line.salesPrices.find(({ currencyId }) => currencyId === newCurrency?.id);

                    if (!salesPrices) {
                        return {
                            ...line,
                            defaultRate: null,
                            unitPrice: null,
                            amount: null,
                            taxAmount: null,
                            grossAmount: null,
                        };
                    }

                    const priceLevelList = mapPriceLevelList(priceLevels, salesPrices);
                    const priceLevel = line.priceLevel
                        ? priceLevelList.find(({ id }) => id === line.priceLevel?.id) ?? null
                        : getBasePriceLevel(priceLevelList);

                    return {
                        ...line,
                        ...calculateLineAmounts(priceLevel, line.quantity, line.taxRate, newCurrencyPrecision),
                        priceLevel,
                        priceLevelList,
                    };
                });
            });

            changeNetSuiteRequest();
        },
        [currencies, priceLevels, setInvoiceItemList, changeNetSuiteRequest]
    );

    const updateCustomField = useUpdateLineCustomField(setInvoiceItemList);

    return {
        onItemChange,
        onPriceLevelChange,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateLineItem,
        updateItemListOnCurrencyChange,
        updateCustomField,
        onAddLine,
        onReorderLines,
        onRemoveLine,
        onCloneLine,
        isLoadingPriceLevels: isLoading,
    };
};
